import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageLayout } from '@/components/ui/page-layout';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Users, 
  Building, 
  User, 
  Clock, 
  ArrowRight, 
  CheckCircle,
  Lightbulb,
  MessageCircle,
  Star,
  Heart,
  Shield,
  Target
} from 'lucide-react';
import { LectureDesignModal } from '@/components/modals/LectureDesignModal';

const consultationTypes = [
  {
    title: "Lecture de Design",
    description: "Une analyse complète de votre Human Design pour comprendre votre énergie unique et optimiser votre potentiel.",
    icon: <Lightbulb className="w-8 h-8 text-amber-600" />,
    duration: "2 heures",
    price: "180€",
    features: [
      "Analyse de votre carte Human Design",
      "Identification de vos centres énergétiques",
      "Stratégie personnalisée d'alignement",
      "Recommandations pratiques",
      "Support écrit post-consultation"
    ]
  },
  {
    title: "Coaching Énergétique",
    description: "Un accompagnement sur mesure pour vous aider à surmonter les blocages énergétiques et atteindre vos objectifs.",
    icon: <Star className="w-8 h-8 text-purple-600" />,
    duration: "5 séances de 1h",
    price: "450€",
    features: [
      "Analyse approfondie de votre design",
      "Plan d'action personnalisé",
      "Exercices pratiques entre les séances",
      "Suivi de progression",
      "Ajustements en temps réel"
    ]
  },
  {
    title: "Consultation Entreprise",
    description: "Optimisez la dynamique d'équipe et la performance de votre entreprise grâce à l'analyse Human Design collective.",
    icon: <Building className="w-8 h-8 text-blue-600" />,
    duration: "Sur mesure",
    price: "Sur devis",
    features: [
      "Analyse des profils énergétiques de l'équipe",
      "Cartographie des interactions optimales",
      "Stratégies de communication adaptées",
      "Optimisation des rôles et responsabilités",
      "Plan d'action pour l'entreprise"
    ]
  }
];

const values = [
  {
    icon: <Heart className="w-6 h-6 text-pink-500" />,
    title: "Bienveillance",
    description: "Nous accompagnons chaque personne avec empathie et respect dans son parcours énergétique."
  },
  {
    icon: <Star className="w-6 h-6 text-yellow-500" />,
    title: "Excellence",
    description: "Nous nous engageons à fournir des outils précis et des conseils personnalisés de haute qualité."
  },
  {
    icon: <Target className="w-6 h-6 text-blue-500" />,
    title: "Innovation",
    description: "Nous développons constamment nos outils pour offrir la meilleure expérience possible."
  }
];

export default function ConsultationsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <PageLayout showBackButton={false}>
      {/* Hero Section */}
      <div className="relative min-h-[80vh] flex items-center justify-center overflow-hidden px-4">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1517960413843-0aee8e2b3285?q=80&w=1999&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'brightness(0.3)'
          }}
        />

        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 z-1"
          style={{
            background: 'linear-gradient(to bottom, rgba(84, 7, 91, 0.9), rgba(147, 51, 234, 0.7))'
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mb-12"
            >
              <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl inline-block">
                <Calendar className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
              </div>
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Consultations & Accompagnements
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Découvrez nos services personnalisés pour vous aider à optimiser votre énergie et atteindre votre plein potentiel
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <Button 
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto bg-white text-purple-900 hover:bg-white/90 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Réserver une consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="w-full sm:w-auto border-white text-white hover:bg-white/10 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full"
                onClick={() => window.location.href = "https://wa.me/votre-numero"}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Discuter avec une assistante
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Consultation Types */}
      <div className="py-20 px-4 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {consultationTypes.map((consultation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <Card className="hover:shadow-lg transition-all h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-3 bg-purple-100 rounded-xl mb-4">
                        {consultation.icon}
                      </div>
                      <h2 className="text-xl font-bold">{consultation.title}</h2>
                      <div className="flex items-center gap-2 text-gray-600 mt-2">
                        <Clock className="w-4 h-4" />
                        <span>{consultation.duration}</span>
                      </div>
                      <p className="text-[#54075B] font-bold text-xl mt-2">{consultation.price}</p>
                    </div>
                    
                    <p className="text-gray-600 text-center mt-4 mb-6">
                      {consultation.description}
                    </p>
                    
                    <div className="space-y-2 flex-grow">
                      <h3 className="font-medium text-gray-900 text-center">Ce qui est inclus :</h3>
                      <ul className="space-y-2">
                        {consultation.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <Button className="w-full mt-6 bg-[#54075B] hover:bg-[#54075B]/90">
                      Réserver une consultation
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Nos Valeurs</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Notre approche est fondée sur des valeurs fortes qui guident chacune de nos interactions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <Card className="hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="p-3 bg-purple-100 rounded-xl inline-block mb-4">
                      {value.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-20 px-4 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="bg-gradient-to-r from-[#54075B] to-purple-700 text-white">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-4">Des questions ?</h2>
              <p className="text-lg mb-6">
                N'hésitez pas à nous contacter pour en savoir plus sur nos lectures de design.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => setIsModalOpen(true)}
                  variant="secondary"
                  size="lg"
                  className="gap-2"
                >
                  Réserver une consultation
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-white border-white hover:bg-white/10 gap-2"
                  onClick={() => window.location.href = "https://wa.me/votre-numero"}
                >
                  <MessageCircle className="w-5 h-5" />
                  Discuter avec une assistante
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal */}
      <LectureDesignModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </PageLayout>
  );
}